import React, { useEffect, useState } from "react";
import { useQuickBooksSync, QbCustomerPreviewRow } from "../hooks/useQuickBooksSync";
import CloseIcon from "@mui/icons-material/Close";

interface QuickBooksCustomerSyncDialogProps {
  onClose: () => void;
  onImported?: () => void;
}

interface StagedCustomer extends QbCustomerPreviewRow {
  selected: boolean;
}

const dialogOverlayStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const dialogStyle: React.CSSProperties = {
  backgroundColor: "white",
  borderRadius: "8px",
  boxShadow: "0 20px 25px rgba(0, 0, 0, 0.15)",
  maxWidth: "1100px",
  width: "95%",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};

const dialogHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "24px",
  borderBottom: "1px solid #e5e7eb",
};

const closeButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  fontSize: "24px",
  cursor: "pointer",
  color: "#6b7280",
};

const dialogContentStyle: React.CSSProperties = {
  flex: 1,
  overflow: "auto",
  padding: "24px",
};

const dialogFooterStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  padding: "16px 24px",
  borderTop: "1px solid #e5e7eb",
};

const submitButtonStyle: React.CSSProperties = {
  padding: "10px 24px",
  backgroundColor: "#10b981",
  color: "white",
  border: "none",
  borderRadius: "6px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const cancelButtonStyle: React.CSSProperties = {
  padding: "10px 24px",
  backgroundColor: "#f3f4f6",
  color: "#374151",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const errorStyle: React.CSSProperties = {
  padding: "12px",
  backgroundColor: "#fee2e2",
  color: "#dc2626",
  borderRadius: "6px",
  fontSize: "13px",
  marginBottom: "16px",
};

const cellInputStyle: React.CSSProperties = {
  width: "100%",
  padding: "6px 8px",
  border: "1px solid #d1d5db",
  borderRadius: "4px",
  fontSize: "13px",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  fontSize: "12px",
  fontWeight: 600,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  padding: "8px",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  padding: "6px 8px",
  borderBottom: "1px solid #f3f4f6",
  verticalAlign: "top",
};

const badgeStyle: React.CSSProperties = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: "999px",
  fontSize: "11px",
  fontWeight: 600,
  backgroundColor: "#dbeafe",
  color: "#1d4ed8",
  whiteSpace: "nowrap",
};

export default function QuickBooksCustomerSyncDialog({
  onClose,
  onImported,
}: QuickBooksCustomerSyncDialogProps) {
  const { previewCustomerSync, importCustomers, isSyncingCustomers } = useQuickBooksSync();
  const [rows, setRows] = useState<StagedCustomer[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingPreview(true);
      setError(null);
      const result = await previewCustomerSync();
      if (cancelled) return;
      setLoadingPreview(false);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      const staged = (result.customers || [])
        .filter((c) => !c.alreadyLinked)
        .map((c) => ({ ...c, selected: true }));
      setRows(staged);
      if (staged.length === 0) {
        setError("No new QuickBooks customers to sync — everything is already imported.");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateRow = (index: number, patch: Partial<StagedCustomer>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const selectedCount = rows.filter((r) => r.selected).length;

  const handleImport = async () => {
    const selected = rows.filter((r) => r.selected);
    if (selected.length === 0) return;

    setError(null);
    setResultMessage(null);
    const result = await importCustomers(selected);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setResultMessage(result.message);
    onImported?.();
    if (!result.errors || result.errors.length === 0) {
      onClose();
    }
  };

  return (
    <div style={dialogOverlayStyle}>
      <div style={dialogStyle}>
        <div style={dialogHeaderStyle}>
          <h2 style={{ margin: 0, fontSize: "20px", color: "#111827", fontWeight: 700 }}>
            Sync Quickbooks Customers
          </h2>
          <button style={closeButtonStyle} onClick={onClose} title="Close">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div style={dialogContentStyle}>
          {error && <div style={errorStyle}>{error}</div>}
          {resultMessage && (
            <div style={{ ...errorStyle, backgroundColor: "#d1fae5", color: "#047857" }}>
              {resultMessage}
            </div>
          )}

          {loadingPreview ? (
            <div className="loading-message">Loading customers from QuickBooks...</div>
          ) : rows.length > 0 ? (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={thStyle}>
                    <input
                      type="checkbox"
                      checked={rows.every((r) => r.selected)}
                      onChange={(e) =>
                        setRows((prev) => prev.map((r) => ({ ...r, selected: e.target.checked })))
                      }
                    />
                  </th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Phone</th>
                  <th style={thStyle}>Street</th>
                  <th style={thStyle}>City</th>
                  <th style={thStyle}>State</th>
                  <th style={thStyle}>ZIP</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row.qbCustomerId}>
                    <td style={tdStyle}>
                      <input
                        type="checkbox"
                        checked={row.selected}
                        onChange={(e) => updateRow(index, { selected: e.target.checked })}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        style={cellInputStyle}
                        value={row.name}
                        onChange={(e) => updateRow(index, { name: e.target.value })}
                        disabled={!row.selected}
                      />
                      {row.matchedCustomerId && (
                        <div style={{ marginTop: "4px" }}>
                          <span style={badgeStyle}>Will link to existing customer</span>
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <input
                        style={cellInputStyle}
                        value={row.email || ""}
                        onChange={(e) => updateRow(index, { email: e.target.value })}
                        disabled={!row.selected}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        style={cellInputStyle}
                        value={row.phone || ""}
                        onChange={(e) => updateRow(index, { phone: e.target.value })}
                        disabled={!row.selected}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        style={cellInputStyle}
                        value={row.street || ""}
                        onChange={(e) => updateRow(index, { street: e.target.value })}
                        disabled={!row.selected}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        style={cellInputStyle}
                        value={row.city || ""}
                        onChange={(e) => updateRow(index, { city: e.target.value })}
                        disabled={!row.selected}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        style={{ ...cellInputStyle, width: "48px" }}
                        maxLength={2}
                        value={row.state || ""}
                        onChange={(e) =>
                          updateRow(index, { state: e.target.value.toUpperCase().slice(0, 2) })
                        }
                        disabled={!row.selected}
                      />
                    </td>
                    <td style={tdStyle}>
                      <input
                        style={cellInputStyle}
                        value={row.zip || ""}
                        onChange={(e) => updateRow(index, { zip: e.target.value })}
                        disabled={!row.selected}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>

        <div style={dialogFooterStyle}>
          <span style={{ fontSize: "13px", color: "#6b7280" }}>
            {rows.length > 0 && `${selectedCount} of ${rows.length} selected`}
          </span>
          <div style={{ display: "flex", gap: "12px" }}>
            <button style={cancelButtonStyle} onClick={onClose} disabled={isSyncingCustomers}>
              Cancel
            </button>
            <button
              style={submitButtonStyle}
              onClick={handleImport}
              disabled={isSyncingCustomers || selectedCount === 0}
            >
              {isSyncingCustomers ? "Importing..." : `Import ${selectedCount} Customer${selectedCount === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
